import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Dialog, DialogContent } from '@mui/material'
import { Stack, Typography, Button } from '@mui/material'
import { Auth } from 'aws-amplify'
import { clearLocalOrgInfo } from 'state/auth'
import { useTheme } from '@mui/material/styles'

const InactivityHandler: React.FC = () => {
  const theme = useTheme()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null)

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current)
    }

    if (!isModalOpen) {
      inactivityTimerRef.current = setTimeout(
        () => {
          setIsModalOpen(true)
        },
        60 * 60 * 1000, // 1 hour
      )
    }
  }, [isModalOpen])

  const handleActivity = useCallback(() => {
    if (!isModalOpen) {
      resetInactivityTimer()
    }
  }, [resetInactivityTimer, isModalOpen])

  const handleSignOut = async () => {
    await Auth.signOut()
    clearLocalOrgInfo()
    window.location.reload()
  }

  const handleContinueSession = useCallback(() => {
    window.location.reload()
  }, [])

  useEffect(() => {
    // List of events to detect user activity
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart']

    // Attach event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity)
    })

    // Initialize the inactivity timer
    resetInactivityTimer()

    // Cleanup event listeners on unmount
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity)
      })
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current)
    }
  }, [handleActivity, resetInactivityTimer])

  return (
    <Dialog
      open={isModalOpen}
      onClose={(_event, reason) => {
        if (reason !== 'backdropClick') {
          handleContinueSession()
        }
      }}
      aria-labelledby="session-timeout-title"
      aria-describedby="session-timeout-description"
      disableEscapeKeyDown
      sx={{
        '& .MuiDialog-paper': {
          backgroundColor: theme.palette.background.paper,
        },
      }}
    >
      <DialogContent>
        <Stack spacing={2} alignItems="center" justifyContent="center">
          <Typography variant="h6" id="session-timeout-title" sx={{ color: theme.palette.text.primary }}>
            Are you still there?
          </Typography>
          <Typography
            variant="body1"
            align="center"
            id="session-timeout-description"
            sx={{ color: theme.palette.text.primary }}
          >
            You have been inactive for a while. Would you like to continue your session or sign out?
          </Typography>
          <Stack direction="column" spacing={2}>
            <Button variant="contained" color="primary" onClick={handleContinueSession} sx={{ width: '200px' }}>
              Continue Session
            </Button>
            <Button variant="outlined" color="error" onClick={handleSignOut} sx={{ width: '200px' }}>
              Sign Out
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default InactivityHandler
